import { Chip } from "@/components/iris";

/**
 * IR-661 Rotem reopen (2026-05-11): per-action env selector.
 *
 * Operator MUST pick which environment a mutating action targets — no default
 * is provided. Rendered inside every delete/edit confirm Sheet on the data
 * surface (group-delete-sheet, delete-selected-dialog, group-edit-sheet,
 * entity-edit-sheet). The host Sheet keeps its primary action disabled
 * until `value !== null`.
 *
 * Tokens mirror entities-env-tab.tsx (purple for prod, cyan for dev) so the
 * env affordance reads consistently across the surface.
 */

export type TargetEnv = "prod" | "dev" | "both";

interface Props {
  /** Selected env. `null` = nothing picked yet → host disables the primary action. */
  value: TargetEnv | null;
  onChange: (next: TargetEnv) => void;
  /** Used for aria/labelling in the host Sheet. */
  id?: string;
  /** Customizable verb in the helper copy ("delete", "edit", etc.). */
  actionVerb?: string;
}

export function TargetEnvRadio({ value, onChange, id, actionVerb = "apply" }: Props) {
  return (
    <fieldset
      id={id}
      style={{
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: "var(--iris-r-md)",
        padding: "10px 12px",
        margin: 0,
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
      aria-required="true"
    >
      <legend
        style={{
          padding: "0 6px",
          fontSize: 11,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--iris-text-secondary, rgba(255,255,255,0.65))",
        }}
      >
        Target environment (required)
      </legend>
      <div style={{ display: "flex", gap: 6 }}>
        <Chip active={value === "prod"} onClick={() => onChange("prod")} aria-label="Production">
          <span style={{ color: value === "prod" ? "var(--iris-env-prod)" : undefined }}>
            Production
          </span>
        </Chip>
        <Chip active={value === "dev"} onClick={() => onChange("dev")} aria-label="Development">
          <span style={{ color: value === "dev" ? "var(--iris-env-dev)" : undefined }}>
            Development
          </span>
        </Chip>
        <Chip active={value === "both"} onClick={() => onChange("both")} aria-label="Both">
          Both
        </Chip>
      </div>
      <div
        style={{
          fontSize: 11,
          color: "var(--iris-text-tertiary, rgba(255,255,255,0.45))",
        }}
      >
        {value === null
          ? `Choose which environment(s) to ${actionVerb} before continuing.`
          : value === "both"
            ? `Will ${actionVerb} on BOTH prod and dev.`
            : `Will ${actionVerb} on ${value === "prod" ? "production" : "development"} only.`}
      </div>
    </fieldset>
  );
}
